<template>
  <div class="project-tasks-tab">
    <!-- Table Toolbar -->
    <table-toolbar
      :show-title="true"
      title="Tasks"
      :show-count="true"
      :current-count="sortedTasks.length"
      :total-count="tasks.length"
      :searchable="true"
      :search-query="tasksSearchQuery"
      @search-change="tasksSearchQuery = $event"
    >
      <template #action-buttons>
        <themed-button
          v-if="canCreateTask"
          variant="default"
          size="default"
          @click="openCreateModal"
          aria-label="New task"
        >
          <theme-icon icon="plus-icon" type="icons" classes="w-4 h-4" />
          <span>New Task</span>
        </themed-button>
      </template>
    </table-toolbar>

    <!-- Tasks Table -->
    <div class="project-tasks-tab__content">

      <themed-table
        v-if="sortedTasks.length > 0"
        :columns="tableColumns"
        :data="sortedTasks"
        :pagination="false"
        :searchable="false"
        :sortable="true"
        hoverable
        bordered
        aria-label="Project tasks table"
      >
        <!-- Priority Column Slot -->
        <template #column-priority="{ row }">
          <div class="project-tasks-tab__priority-badge" :class="getPriorityBadgeClass(row.priority)">
            <span>{{ row.priority }}</span>
          </div>
        </template>

        <!-- Completion Status Column Slot -->
        <template #column-is_complete="{ row }">
          <span class="project-tasks-tab__completion-status">
            {{ row.is_complete ? 'Yes' : 'No' }}
          </span>
        </template>

        <!-- Actions Column Slot -->
        <template #actions="{ row }">
          <div class="project-tasks-tab__actions">
            <button
              v-if="canEditTask(row.project_id)"
              @click.stop="openEditModal(row)"
              class="project-tasks-tab__action-button"
              :aria-label="'Edit task: ' + row.task"
            >
              <theme-icon icon="edit" type="icons" classes="w-5 h-5" />
            </button>
            
            <button
              v-if="canDeleteTask(row.project_id)"
              @click.stop="confirmDeleteTask(row.id)"
              class="project-tasks-tab__action-button project-tasks-tab__action-button--delete"
              :aria-label="'Delete task: ' + row.task"
            >
              <theme-icon icon="trash" type="icons" classes="w-5 h-5" />
            </button>
          </div>
        </template>
      </themed-table>

      <!-- Empty State -->
      <div v-else class="project-tasks-tab__empty">
        <theme-icon icon="clipboard" type="icons" classes="w-16 h-16" />
        <p class="project-tasks-tab__empty-message">
          {{ tasksSearchQuery ? 'No tasks match your search.' : 'No tasks found' }}
        </p>
      </div>
    </div>

    <!-- Task Modal (Create/Edit) -->
    <edit-task-modal
      :project-id="projectId"
      :task="selectedTask"
      :is-edit-mode="isEditMode"
      @task-created="handleTaskCreated"
      @task-updated="handleTaskUpdated"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ThemedTable from '../ThemedTable.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';
import EditTaskModal from './EditTaskModal.vue';
import TableToolbar from '../table/TableToolbar.vue';

export default {
  name: 'ProjectTasksTab',
  
  components: {
    ThemedTable,
    ThemedButton,
    ThemeIcon,
    EditTaskModal,
    TableToolbar
  },
  
  props: {
    projectId: {
      type: Number,
      required: true
    }
  },
  
  data() {
    return {
      selectedTask: null,
      isEditMode: false,
      isDeleting: false,
      tasksSearchQuery: ''
    };
  },
  
  computed: {
    ...mapState(['projects']),
    
    tasks() {
      return this.projects.selectedProject?.tasks || [];
    },
    
    filteredTasks() {
      let tasks = this.tasks;
      
      // Apply search filter
      if (this.tasksSearchQuery) {
        const query = this.tasksSearchQuery.toLowerCase();
        tasks = tasks.filter(task => 
          task.task?.toLowerCase().includes(query) ||
          task.task_category?.toLowerCase().includes(query) ||
          task.priority?.toLowerCase().includes(query)
        );
      }
      
      return tasks;
    },
    
    sortedTasks() {
      // Sort filtered tasks by start_date_formatted and start_time_formatted in ascending order
      return _.orderBy(this.filteredTasks, ['start_date_formatted', 'start_time_formatted'], ['asc', 'asc']);
    },
    
    canCreateTask() {
      return this.$gate.can('create', 'project');
    },
    
    tableColumns() {
      return [
        {
          field: 'task_category',
          label: 'Type',
          sortable: true,
          searchable: true
        },
        {
          field: 'start_formatted',
          label: 'Start',
          sortable: true,
          searchable: false
        },
        {
          field: 'end_formatted',
          label: 'End',
          sortable: true,
          searchable: false
        },
        {
          field: 'priority',
          label: 'Priority',
          sortable: true,
          searchable: true,
          type: 'custom'
        },
        {
          field: 'task',
          label: 'Task',
          sortable: true,
          searchable: true
        },
        {
          field: 'is_complete',
          label: 'Completed?',
          sortable: true,
          searchable: false,
          type: 'custom'
        },
        {
          field: 'actions',
          label: '',
          sortable: false,
          searchable: false,
          type: 'actions'
        }
      ];
    }
  },
  
  methods: {
    openCreateModal() {
      this.selectedTask = null;
      this.isEditMode = false;
      this.$modal.show('edit-task-modal');
    },
    
    openEditModal(task) {
      this.selectedTask = task;
      this.isEditMode = true;
      this.$nextTick(() => {
        this.$modal.show('edit-task-modal');
      });
    },
    
    canEditTask(projectId) {
      return this.$gate.can('update', 'project', projectId);
    },
    
    canDeleteTask(projectId) {
      return this.$gate.can('delete', 'project', projectId);
    },
    
    getPriorityBadgeClass(priority) {
      const classMap = {
        'Urgent': 'project-tasks-tab__priority-badge--urgent',
        'High': 'project-tasks-tab__priority-badge--high',
        'Medium': 'project-tasks-tab__priority-badge--medium',
        'Low': 'project-tasks-tab__priority-badge--low'
      };
      
      return classMap[priority] || 'project-tasks-tab__priority-badge--low';
    },
    
    confirmDeleteTask(taskId) {
      this.$swal({
        title: 'Delete this task?',
        text: 'ALL data will be lost',
        type: 'warning',
        buttonsStyling: false,
        confirmButtonText: 'Yes - Delete it',
        confirmButtonClass: 'btn btn-red',
        showCancelButton: true,
        cancelButtonClass: 'btn btn-secondary mr-4',
        reverseButtons: true
      }).then(async (result) => {
        if (result.value) {
          await this.deleteTask(taskId);
        }
      });
    },
    
    async deleteTask(taskId) {
      this.isDeleting = true;
      
      try {
        await axios.delete(`/project/tasks/${taskId}`);
        
        // Update Vuex store - remove task from array
        this.projects.selectedProject.tasks = this.projects.selectedProject.tasks.filter(
          task => task.id !== taskId
        );
        
        // Show success notification
        this.$notify({
          type: 'success',
          title: 'Success',
          text: 'Task deleted successfully'
        });
      } catch (error) {
        console.error('Error deleting task:', error);
        
        // Show error notification
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to delete task'
        });
      } finally {
        this.isDeleting = false;
      }
    },
    
    handleTaskCreated(task) {
      // Add new task to Vuex store
      this.projects.selectedProject.tasks.push(task);
      
      // Close modal
      this.$modal.hide('create-task-modal');
      
      // Show success notification
      this.$notify({
        type: 'success',
        title: 'Success',
        text: 'Task created successfully'
      });
    },
    
    handleTaskUpdated(updatedTask) {
      // Update task in Vuex store
      const index = this.projects.selectedProject.tasks.findIndex(
        task => task.id === updatedTask.id
      );
      
      if (index !== -1) {
        this.$set(this.projects.selectedProject.tasks, index, updatedTask);
      }
      
      // Close modal and clear selected task
      this.$modal.hide('edit-task-modal');
      this.selectedTask = null;
      
      // Show success notification
      this.$notify({
        type: 'success',
        title: 'Success',
        text: 'Task updated successfully'
      });
    }
  }
};
</script>
